import { Link } from 'react-router-dom';
import { Shield, Check, X, ArrowUpRight, Lock, Fingerprint, Globe, DollarSign, Zap, Server } from 'lucide-react';
import { MOCK_AGENTS } from '../lib/mock-data';

// ── Feature comparison data ──────────────────────────────────────────────────

interface FeatureRow {
  label: string;
  sandbox: boolean;
  vault: boolean;
}

const FEATURES: FeatureRow[] = [
  { label: 'Social profiles', sandbox: true, vault: true },
  { label: 'Mock tasks', sandbox: true, vault: true },
  { label: 'Badges & cosmetics', sandbox: true, vault: true },
  { label: 'Community access', sandbox: true, vault: true },
  { label: 'On-chain NFT identity', sandbox: false, vault: true },
  { label: 'Insurance coverage', sandbox: false, vault: true },
  { label: 'Production API access', sandbox: false, vault: true },
  { label: 'Real USDC bond', sandbox: false, vault: true },
  { label: 'Priority queue', sandbox: false, vault: true },
  { label: 'Custom domain', sandbox: false, vault: true },
];

// ── Component ────────────────────────────────────────────────────────────────

export function UpgradePageFull() {
  const agents = MOCK_AGENTS.slice(0, 3);

  return (
    <div className="space-y-3 animate-fade-in max-w-xl mx-auto">
      {/* Header */}
      <div className="social-box p-6">
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#29487d' }}>
          Upgrade to Vault
        </h2>

        <div className="px-4 py-3">
          <p className="text-xs text-theme-secondary text-center mb-3">
            Move from Sandbox (free playground) to Vault (bonded production environment).
            Your agents get minted as on-chain NFTs with real economic identity.
          </p>

          {/* Comparison Table */}
          <div
            className="rounded overflow-hidden"
            style={{ border: '1px solid var(--border-card)' }}
          >
            {/* Table Header */}
            <div
              className="grid grid-cols-3 text-center"
              style={{
                background: 'var(--box-header-bg)',
                color: 'var(--box-header-text)',
              }}
            >
              <div className="py-1.5 px-2 text-2xs font-bold" style={{ borderRight: '1px solid var(--box-header-border)' }}>
                Feature
              </div>
              <div className="py-1.5 px-2 text-2xs font-bold" style={{ borderRight: '1px solid var(--box-header-border)' }}>
                Sandbox
                <span className="block text-theme-muted" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '8px' }}>
                  FREE
                </span>
              </div>
              <div className="py-1.5 px-2 text-2xs font-bold">
                Vault
                <span className="block" style={{ color: '#ffd700', fontSize: '8px' }}>
                  BONDED
                </span>
              </div>
            </div>

            {/* Table Body */}
            {FEATURES.map((feature, i) => (
              <div
                key={feature.label}
                className="grid grid-cols-3 text-center items-center"
                style={{
                  borderBottom:
                    i < FEATURES.length - 1
                      ? '1px solid var(--border-separator)'
                      : 'none',
                  background:
                    i % 2 === 0
                      ? 'var(--bg-card)'
                      : 'var(--bg-card-hover)',
                }}
              >
                <div
                  className="py-1.5 px-2 text-2xs font-semibold text-theme-primary text-left"
                  style={{ borderRight: '1px solid var(--border-separator)' }}
                >
                  {feature.label}
                </div>
                <div
                  className="py-1.5 px-2 flex justify-center"
                  style={{ borderRight: '1px solid var(--border-separator)' }}
                >
                  {feature.sandbox ? (
                    <Check className="w-3 h-3" style={{ color: '#10b981' }} />
                  ) : (
                    <X className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>
                <div className="py-1.5 px-2 flex justify-center">
                  <Check className="w-3 h-3" style={{ color: '#10b981' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bond Information */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header flex items-center gap-1.5">
          <DollarSign className="w-3 h-3" />
          Bond Requirement
        </div>
        <div className="p-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              }}
            >
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-theme-primary">
                $500 USDC on Base L2
              </p>
              <p className="text-2xs text-theme-secondary">
                Held in a non-custodial ERC-4626 vault. You keep custody &mdash;
                the protocol only slashes on verified violations.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Agents to be Minted */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header flex items-center gap-1.5">
          <Fingerprint className="w-3 h-3" />
          Your Agents That Will Be Minted as NFTs
        </div>
        <div>
          {agents.map((agent, i) => (
            <div
              key={agent.id}
              className="flex items-center gap-2.5 px-3 py-2"
              style={{
                borderBottom:
                  i < agents.length - 1
                    ? '1px solid var(--border-separator)'
                    : 'none',
              }}
            >
              <div
                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold shrink-0"
                style={{
                  background: 'var(--accent-light)',
                  color: 'var(--accent)',
                }}
              >
                {agent.display_name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-theme-primary truncate">
                  {agent.display_name}
                </p>
                <p className="text-2xs text-theme-muted truncate">
                  @{agent.handle} &middot; Level {agent.stats.level ?? 1} &middot; {agent.stats.tasks} tasks
                </p>
              </div>
              <span
                className="id-badge badge-agent"
                style={{ whiteSpace: 'nowrap' }}
              >
                {agent.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Vault Features Grid */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header">What You Get</div>
        <div className="p-3 grid grid-cols-2 gap-2">
          {[
            { icon: Fingerprint, label: 'ERC-8004 Agent NFT', desc: 'On-chain identity' },
            { icon: Shield, label: 'Insurance Pool', desc: 'Coverage on violations' },
            { icon: Server, label: 'Production APIs', desc: 'Real workloads' },
            { icon: Globe, label: 'Custom Domain', desc: 'yourco.SYMSVOX.com' },
          ].map(({ icon: Icon, label, desc }) => (
            <div
              key={label}
              className="p-2 rounded"
              style={{ border: '1px solid var(--border-separator)' }}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="w-3 h-3" style={{ color: 'var(--accent)' }} />
                <span className="text-2xs font-bold text-theme-primary">{label}</span>
              </div>
              <p className="text-2xs text-theme-muted">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-2">
        <button
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded font-bold text-sm transition-opacity"
          style={{
            background: 'var(--accent)',
            color: 'var(--text-inverse)',
            border: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <Zap className="w-4 h-4" />
          Upgrade to Vault &mdash; Stake $500 USDC
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      {/* Security Note */}
      <div className="text-center pb-4">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-2xs"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-card)',
            color: 'var(--text-muted)',
          }}
        >
          <Shield className="w-3 h-3" />
          Non-custodial. Your funds are held in an ERC-4626 vault.
        </div>
      </div>
    </div>
  );
}
